import React, { Component } from 'react';
import SimplePanel from '@/shared/components/SimplePanel';
import { FormattedMessage, injectIntl } from 'react-intl';
import intlShape from '@/shared/utils/intlShape';
import api from '@/shared/api';
import config from '@/config';
import { injectStateService } from '@/shared/services/StateService';
import handleHttpError from '@/shared/services/HandleHttpError';
import messages from '../hrmMessages';
import Axios from 'axios';
import EmployeeRelationMatrixFilters, {
  defaultFilters as defaultRelationMatrixFilters,
} from '@/modules/hrm/assignmentAnalysis/EmployeeRelationMatrixFilters';
import Button from '@/shared/components/Button';
import { FaCog, FaFileExcel } from 'react-icons/fa';
import CheckboxListModal from '@/shared/components/CheckboxListModal';
import { ColumnHeader, ScrollableTable } from '@/shared/components/table';
import PropTypes from 'prop-types';
import NavigateToState from '@/shared/components/NavigateToState';
import { functionalities } from '@/shared/services/AuthService';
import userStorage from '@/shared/services/userStorage';
import {
  initHiddenColumnsState,
  handleHiddenColumnsSelection,
} from '@/shared/utils/hiddenColumnsHelper';
import CapacityReportFilters, {
  defaultFilters as defaultCapacityFilters,
} from '@/modules/hrm/assignmentAnalysis/CapacityReportFilters';
import { emptyGuid, guid1 } from '@/shared/utils/guid';
import SignalRService from '@/shared/services/SignalRService';
import CapacityPlanningReportTable from '@/modules/hrm/assignmentAnalysis/CapacityPlanningReportTable';
import { listItemMsg } from '@/shared/components/FormattedListValue';
import LoadingAnimation from '@/shared/components/LoadingAnimation';
import moment from 'moment';
import ToastrService from '@/shared/services/ToastrService';

const capacityStaticColumnsStorageKey = 'CapacityReportStaticHiddenColumns';
const capacityColumnDefinitions = [
  {
    id: 'employeeRole',
    message: messages.EmployeeRole,
    visibleByDefault: false,
  },
  {
    id: 'fullWeeklyCapacity',
    message: messages.EmployeeFullWeeklyCapacity,
    visibleByDefault: false,
  },
  {
    id: 'weeklyCapacity',
    message: messages.EmployeeWeeklyCapacity,
    visibleByDefault: true,
  },
  {
    id: 'yearlyCapacity',
    message: messages.EmployeeYearlyCapacity,
    visibleByDefault: false,
  },
  {
    id: 'totalCapacity',
    message: messages.EmployeeTotalCapacity,
    visibleByDefault: false,
  },
  {
    id: 'remainingCapacity',
    message: messages.EmployeeRemainingCapacity,
    visibleByDefault: true,
  },
  {
    id: 'availableCapacity',
    message: messages.EmployeeAvailableCapacity,
    visibleByDefault: true,
  },
];

const getSessionStorageKey = matrixType => {
  switch (matrixType) {
    case 'relation':
      return 'AssignmentAnalysisRelationMatrix';
    case 'task':
      return 'AssignmentAnalysisTaskMatrix';
    case 'capacity':
      return 'AssignmentAnalysisCapacityReport';
  }
};

const getColumnSelectionStorageKey = matrixType => {
  switch (matrixType) {
    case 'relation':
      return 'RelationMatrixHiddenColumns';
    case 'task':
      return 'TaskMatrixHiddenColumns';
    case 'capacity':
      return 'RelationCapacityMatrixHiddenColumns';
  }
};

const getZendeskArticle = matrixType => {
  switch (matrixType) {
    case 'relation':
      return '21392672273052';
    case 'task':
      return '21392660992924';
    case 'capacity':
      return '24671462481692';
  }
};

class AssignmentAnalysisMatrix extends Component {
  constructor(props) {
    super(props);
    const { matrixType, intl } = this.props;
    const sessionStorageKey = getSessionStorageKey(matrixType);
    const columnSelectionStorageKey = getColumnSelectionStorageKey(matrixType);
    const sessionParams = JSON.parse(sessionStorage.getItem(sessionStorageKey)) || {};

    if ('groupByMonth' in sessionParams) {
      sessionParams.groupBy = sessionParams.groupByMonth ? 1 : 0;
      delete sessionParams.groupByMonth;
    }
    if ('groupByPeriod' in sessionParams) {
      sessionParams.groupBy = sessionParams.groupByPeriod;
      delete sessionParams.groupByPeriod;
    }

    const defaultFilters =
      matrixType === 'relation'
        ? defaultRelationMatrixFilters
        : { ...defaultRelationMatrixFilters, ...defaultCapacityFilters };

    this.defaultFilters = defaultFilters;

    const capacityStaticState =
      matrixType === 'capacity'
        ? initHiddenColumnsState(capacityStaticColumnsStorageKey, capacityColumnDefinitions, intl)
        : { hideableColumns: [], columnStatus: {} };

    this.state = {
      sessionStorageKey,
      columnSelectionStorageKey,
      filters: {
        ...defaultFilters,
        ...sessionParams,
      },
      initialSearchParams: {
        ...defaultFilters,
        ...sessionParams,
      },
      items: [],
      exporting: false,
      parametersForLastApiCall: null,
      columns: [],
      hideableColumns: [],
      capacityHideableColumns: capacityStaticState.hideableColumns,
      capacityColumnStatus: capacityStaticState.columnStatus,
      showColumnSelectionModal: false,
      availableMainTaskTemplateIds: [],
      capacityLoading: true,
      employeeProfiles: [],
    };
  }

  componentDidMount() {
    if (this.props.matrixType === 'capacity') {
      this.loadCapacityData();
    }
    this.loadEmployeeProfiles();
  }

  loadEmployeeProfiles = () => {
    api.base
      .get('/api/employeeProfile/Get')
      .then(({ data }) => {
        const profiles = (data || [])
          .map(x => ({ id: x.id, label: x.name }))
          .sort((a, b) => a.label.localeCompare(b.label));
        this.setState({ employeeProfiles: profiles });
      })
      .catch(() => {});
  };

  componentDidUpdate(prevProps, prevState) {
    if (
      this.props.matrixType === 'capacity' &&
      JSON.stringify(prevState.filters) !== JSON.stringify(this.state.filters)
    ) {
      this.loadCapacityData();
    }
  }

  componentWillUnmount() {
    if (this.reportToken && !this.reportToken.token.reason) {
      this.reportToken.cancel('Operation canceled due to leaving page.');
    }
  }

  applySorting(rows, searchParams) {
    const totalRow = '00000000-0000-0000-0000-000000000001';
    const sign = searchParams.desc ? -1 : 1;
    let ordering = (a, b) => 0;
    if (searchParams.sortKey === 'employee') {
      ordering = (a, b) => sign * a.employee.localeCompare(b.employee);
    } else if (searchParams.sortKey === 'employeeRole') {
      ordering = (a, b) =>
        sign * a.employeeRole.localeCompare(b.employeeRole) || a.employee.localeCompare(b.employee);
    } else if (searchParams.sortKey === 'total') {
      ordering = (a, b) => sign * (a.total - b.total) || a.employee.localeCompare(b.employee);
    } else if (searchParams.sortKey === 'employeeRemainingCapacity') {
      ordering = (a, b) =>
        sign * (a.employeeRemainingCapacity - b.employeeRemainingCapacity) ||
        a.employee.localeCompare(b.employee);
    } else if (searchParams.sortKey === 'employeeTotalCapacity') {
      ordering = (a, b) =>
        sign * (a.employeeTotalCapacity - b.employeeTotalCapacity) ||
        a.employee.localeCompare(b.employee);
    } else if (searchParams.sortKey === 'employeeWeeklyCapacity') {
      ordering = (a, b) =>
        sign * (a.employeeWeeklyHours - b.employeeWeeklyHours) ||
        a.employee.localeCompare(b.employee);
    } else if (searchParams.sortKey === 'employeeAvailableCapacity') {
      ordering = (a, b) =>
        sign *
          (a.employeeAvailableHours -
            (a.totalScheduledMinutes || a.total) -
            (b.employeeAvailableHours - (b.totalScheduledMinutes || b.total))) ||
        a.employee.localeCompare(b.employee);
    } else if (searchParams.sortKey !== '') {
      const column = this.state.hideableColumns.findIndex(col => col.id === searchParams.sortKey);
      if (column >= 0) {
        ordering = (a, b) =>
          sign * ((a.counts[column] || 0) - (b.counts[column] || 0)) ||
          a.employee.localeCompare(b.employee);
      }
    }

    // Keep the "unassigned" row before "totals" at the bottom of the list
    return rows.sort((a, b) => {
      if (a.id === totalRow) return 1;
      if (b.id === totalRow) return -1;
      if (a.id === emptyGuid) return 1;
      if (b.id === emptyGuid) return -1;
      return ordering(a, b);
    });
  }

  columnsFromHideableColumns = hideableColumns =>
    hideableColumns.map((c, i) => (c.selected ? i : -1)).filter(c => c >= 0);

  loadContinuation = (serializedParameters, data, users) => {
    const { intl, matrixType } = this.props;
    if (!data) return [];
    const { columnSelectionStorageKey, filters } = this.state;

    const isTaskTemplateColumn = matrixType === 'task' || matrixType === 'capacity';
    const storageState = userStorage.getItem(columnSelectionStorageKey) || {};
    const hideableColumns = data.columns.map(c => ({
      id: c.id,
      label:
        c.id === emptyGuid
          ? intl.formatMessage(messages.WithoutTemplate)
          : isTaskTemplateColumn
          ? intl.formatMessage(listItemMsg('TaskTemplate', c.id, c.label))
          : c.label,
      selected: c.id in storageState ? storageState[c.id] : true,
    }));
    const columns = this.columnsFromHideableColumns(hideableColumns);

    const userMap = {};
    for (const user of users || []) {
      userMap[user.id] = user;
    }

    const newRows = data.rows.filter(row => row.id === emptyGuid || userMap[row.id]);

    for (const row of newRows) {
      const user = userMap[row.id] || {};
      row.employee = user.employee || '';
      row.employeeRole = user.employeeRole || '';
      row.employeeWeeklyHours = user.employeeWeeklyProductivityMinutes || 0;
      row.employeeWeeklyHoursContract = user.employeeWeeklyMinutes || 0;
      row.productivityPercentage = user.productivityPercentage;
      row.employeeAvailableHours = user.employeeAvailableMinutes || 0;
      row.employeeRemainingCapacity = user.employeeRemainingCapacityMinutes || 0;
      row.employeeTotalCapacity = user.employeeTotalCapacityMinutes || 0;
      row.employeeYearlyCapacity = user.employeeYearlyCapacityMinutes || 0;
      row.isActive = user.isActive !== undefined ? user.isActive : true;
      row.periodRemainingCapacity = user.periodRemainingCapacityMinutes || {};
      delete userMap[row.id];
    }

    // When filtering by mainTaskTemplateId, only show employees with matching work
    if (!filters.mainTaskTemplateId) {
      for (const user of Object.values(userMap)) {
        if (user.isActive) {
          newRows.push({
            id: user.id,
            employee: user.employee,
            employeeRole: user.employeeRole,
            employeeWeeklyHours: user.employeeWeeklyProductivityMinutes,
            employeeWeeklyHoursContract: user.employeeWeeklyMinutes,
            productivityPercentage: user.productivityPercentage,
            employeeAvailableHours: user.employeeAvailableMinutes,
            employeeRemainingCapacity: user.employeeRemainingCapacityMinutes,
            employeeTotalCapacity: user.employeeTotalCapacityMinutes,
            employeeYearlyCapacity: user.employeeYearlyCapacityMinutes || 0,
            periodRemainingCapacity: user.periodRemainingCapacityMinutes || {},
            counts: [],
            total: 0,
            totalScheduledMinutes: 0,
            isActive: true,
          });
        }
      }
    }

    this.setState(state => ({
      ...state,
      items: this.applySorting(newRows, ''),
      parametersForLastApiCall: serializedParameters,
      hideableColumns,
      columns,
      availableMainTaskTemplateIds: data.availableMainTaskTemplateIds || [],
    }));
    return newRows;
  };

  errorContinuation = error => {
    const { intl } = this.props;
    if (!Axios.isCancel(error)) return handleHttpError(intl)(error);
  };

  createExcelReport = () => {
    const { filters } = this.state;
    const params = {
      ...filters,
      culture: this.props.intl.locale,
      connectionId: SignalRService.getConnectionId(),
      deadlineFrom: filters.deadlineFrom ? moment(filters.deadlineFrom).toISOString() : null,
      deadlineUntil: filters.deadlineUntil ? moment(filters.deadlineUntil).toISOString() : null,
    };

    this.setState({ exporting: true });

    return api.base
      .post(`${config.appUrls.tasks}/api/CapacityPlanning/EmployeeReportDataExport`, params, {
        params,
      })
      .then(this.handleReportResponse)
      .catch(this.handleReportError);
  };

  handleReportResponse = () => {
    const { intl } = this.props;
    this.setState({ exporting: false });
    ToastrService.info(intl.formatMessage(messages.ExportToExcelStarted));
  };
  handleReportError = error => {
    const { intl } = this.props;
    this.setState({ exporting: false });
    handleHttpError(intl)(error);
  };

  loadItems = searchParams => {
    if (this.reportToken && !this.reportToken.token.reason) {
      this.reportToken.cancel('Operation canceled due to new request.');
    }
    this.reportToken = Axios.CancelToken.source();

    const { filters, sessionStorageKey } = this.state;
    const params = {
      ...filters,
      languageCode: this.props.intl.locale,
      deadlineFrom: filters.deadlineFrom ? moment(filters.deadlineFrom).toISOString() : null,
      deadlineUntil: filters.deadlineUntil ? moment(filters.deadlineUntil).toISOString() : null,
    };
    const serializedParameters = JSON.stringify(params);
    if (this.state.parametersForLastApiCall === serializedParameters) {
      return this.applySorting(this.state.items, searchParams);
    }

    const sessionParams = JSON.stringify(filters);
    sessionStorage.setItem(sessionStorageKey, sessionParams);

    let apiCall = undefined;
    if (this.props.matrixType === 'relation') {
      apiCall = api.base.post(
        `${config.appUrls.api}/api/AssignmentAnalysis/EmployeeRelation`,
        params,
        {
          cancelToken: this.reportToken.token,
        },
      );
    } else if (this.props.matrixType === 'task') {
      apiCall = api.base.post(
        `${config.appUrls.tasks}/api/AssignmentAnalysis/EmployeeTask`,
        params,
        {
          cancelToken: this.reportToken.token,
        },
      );
    } else if (this.props.matrixType === 'capacity') {
      apiCall = api.base.post(
        `${config.appUrls.tasks}/api/CapacityPlanning/EmployeeReport`,
        params,
        {
          params,
          cancelToken: this.reportToken.token,
        },
      );
    }

    const employeeParams = {
      ...params,
      includeCapacityCalculations: this.props.matrixType === 'capacity',
      groupBy: this.props.matrixType === 'capacity' ? filters.groupBy ?? 0 : 0,
    };

    let usersCall = api.base.post(
      `${config.appUrls.api}/api/AssignmentAnalysis/Employees`,
      employeeParams,
      {
        cancelToken: this.reportToken.token,
      },
    );

    return Promise.all([
      apiCall.catch(error => this.errorContinuation(error)),
      usersCall.catch(error => this.errorContinuation(error)),
    ]).then(
      ([response, usersResponse]) =>
        response &&
        usersResponse &&
        this.applySorting(
          this.loadContinuation(serializedParameters, response.data, usersResponse.data),
          searchParams,
        ),
    );
  };

  filtersChanged(filters) {
    let newFilters = {
      ...(this.state.filters || {}),
      ...filters,
    };

    this.setState(state => ({
      filters: newFilters,
      initialSearchParams: {
        ...filters,
      },
      items: [],
      hideableColumns: [],
      columns: [],
    }));
  }

  handleColumnSelection(allItems) {
    const { columnSelectionStorageKey } = this.state;
    const staticIds = new Set(capacityColumnDefinitions.map(c => c.id));
    const staticItems = allItems.filter(c => staticIds.has(c.id));
    const dynamicItems = allItems.filter(c => !staticIds.has(c.id));

    const storageState = dynamicItems.reduce(
      (acc, col) => ({ ...acc, [col.id]: col.selected }),
      {},
    );
    userStorage.setItem(columnSelectionStorageKey, storageState);

    const staticResult = handleHiddenColumnsSelection(
      staticItems,
      this.state.capacityHideableColumns,
      capacityStaticColumnsStorageKey,
    );

    const columns = this.columnsFromHideableColumns(dynamicItems);
    this.setState(state => ({
      ...state,
      showColumnSelectionModal: false,
      hideableColumns: dynamicItems,
      columns,
      capacityHideableColumns: staticResult.hideableColumns,
      capacityColumnStatus: staticResult.columnStatus,
    }));
  }

  loadCapacityData = () => {
    this.setState({ capacityLoading: true });
    Promise.resolve(this.loadItems({ sortKey: '', desc: false }))
      .then(() => this.setState({ capacityLoading: false }))
      .catch(() => this.setState({ capacityLoading: false }));
  };

  renderRow = row => {
    const { columns, filters } = this.state;
    const { intl, matrixType } = this.props;

    // This array is setup to match the backend Enum values of "EmployeeProfile"
    // Index values match those of the enum values...
    const fieldMap = [
      undefined,
      'responsible',
      'assistant',
      'assistantBackup',
      'associate',
      'billingUser',
      'seniorUser',
    ];

    const hours = intl.formatMessage(messages.HoursAbbreviated);

    // Render capacity values (always as hours and minutes)
    const renderCapacityValue = minutes => {
      if (!minutes || minutes === 0) return '-';
      const duration = moment.duration(minutes, 'minutes');
      const hrs = Math.floor(duration.asHours());
      const mins = Math.abs(duration.minutes());
      return mins === 0 ? `${hrs}${hours}` : `${hrs}${hours}${String(mins).padStart(2, '0')}`;
    };

    // Render values based on selected display mode
    const renderValue = x => {
      if (matrixType !== 'capacity') return x;

      const { filters } = this.state;
      if (filters.displayMode === 1 || filters.displayMode === 2) {
        return x === 0 ? '-' : x;
      }

      return renderCapacityValue(x);
    };

    const renderCellContent = (row, column) => {
      const { hideableColumns, filters } = this.state;
      const { matrixType } = this.props;
      const value = renderValue(row.counts[column] || 0);

      let stateName = '';
      let stateParams = {};
      const columnDefinition = hideableColumns[column];
      const columnId = columnDefinition.id;

      if (matrixType === 'relation') {
        const field = fieldMap[parseInt(columnId)];
        if (!!field) {
          stateName = 'relations.overview';
          stateParams = { [field]: row.id };
          if (filters.employeeId) {
            if (!!filters.employeeProfile) {
              stateParams[fieldMap[filters.employeeProfile]] = filters.employeeId;
            } else {
              stateParams['assignedTo'] = filters.employeeId;
            }
          }
        }
      }
      if (!stateName) return <span>{value}</span>;
      return (
        <NavigateToState
          stateName={stateName}
          useUrl
          paramsToJson
          title={intl.formatMessage(
            matrixType === 'relation'
              ? messages.ShowInRelationsOverview
              : messages.ShowInTasksOverview,
          )}
          functionality={functionalities.Relation}
          permission="Read"
          stateParams={stateParams}
        >
          {value}
        </NavigateToState>
      );
    };

    const header = !!row.employee
      ? row.employee
      : row.id !== emptyGuid
      ? intl.formatMessage(messages.Totals)
      : intl.formatMessage(messages.Unassigned);

    return (
      <tbody className="hover" key={'row_' + row.id}>
        <tr>
          <td className={!row.isActive ? 'removed-user' : ''}>{header}</td>
          <td>{row.employeeRole}</td>
          {columns.map(column => (
            <td key={row.id + '_' + column}>{renderCellContent(row, column)}</td>
          ))}
          <td>{renderValue(row.total)}</td>
        </tr>
      </tbody>
    );
  };

  render() {
    const { intl, matrixType } = this.props;
    const {
      initialSearchParams,
      filters,
      items,
      showColumnSelectionModal,
      columns,
      hideableColumns,
      exporting,
      capacityHideableColumns,
      capacityColumnStatus,
    } = this.state;

    let title = '';
    switch (matrixType) {
      case 'relation':
        title = intl.formatMessage(messages.AssignmentAnalysisEmployeeRelationMatrix);
        break;
      case 'task':
        title = intl.formatMessage(messages.AssignmentAnalysisEmployeeTaskMatrix);
        break;
      case 'capacity':
        title = intl.formatMessage(messages.EmployeeCapacityReport);
        break;
    }

    const zendeskArticle = getZendeskArticle(matrixType);
    const showRelationFilters = matrixType === 'relation' || matrixType === 'task';
    const showCapacityFilters = matrixType === 'capacity';
    const { employeeProfiles } = this.state;

    const modalItems =
      matrixType === 'capacity'
        ? [...capacityHideableColumns, ...hideableColumns]
        : hideableColumns;

    return (
      <div className={`relation-matrix${matrixType === 'capacity' ? ' capacity-report-page' : ''}`}>
        {showColumnSelectionModal && (
          <CheckboxListModal
            title={intl.formatMessage(messages.SelectColumns)}
            items={modalItems}
            onSave={c => this.handleColumnSelection(c)}
            onClose={() => this.setState({ showColumnSelectionModal: false })}
            show={showColumnSelectionModal}
            sortByLabel={false}
          />
        )}
        <div className="fixed-filters">
          <SimplePanel article={zendeskArticle} title={title}>
            {showRelationFilters && (
              <div className="info-block container-fluid">
                <EmployeeRelationMatrixFilters
                  filters={filters}
                  columns={hideableColumns || []}
                  onFilterChange={filters => this.filtersChanged(filters)}
                  employeeProfiles={employeeProfiles}
                  showEmployeeAndRoleFilters={matrixType === 'relation'}
                  defaultFilters={this.defaultFilters}
                  screen={
                    matrixType === 'relation'
                      ? 'AssignmentAnalysisRelation'
                      : 'AssignmentAnalysisTask'
                  }
                />
              </div>
            )}
            {showCapacityFilters && (
              <div className="info-block container-fluid">
                <CapacityReportFilters
                  filters={filters}
                  columns={hideableColumns || []}
                  onFilterChange={filters => this.filtersChanged(filters)}
                  availableMainTaskTemplateIds={this.state.availableMainTaskTemplateIds}
                  employeeProfiles={employeeProfiles}
                  defaultFilters={this.defaultFilters}
                />
              </div>
            )}
          </SimplePanel>

          <div className="row btn-row">
            <div className="col-md-6 pull-right content-right">
              <Button
                btnClassName="btn btn-xs btn-default margin10R"
                onClick={() => this.setState({ showColumnSelectionModal: true })}
              >
                <span className="margin5R">
                  <FaCog className="react-icon" />
                </span>
                <FormattedMessage {...messages.ColumnConfig} />
              </Button>
              {matrixType === 'capacity' && (
                <Button
                  btnClassName="btn btn-xs btn-default margin10R"
                  onClick={() => this.createExcelReport()}
                  disabled={exporting}
                >
                  <span className="margin5R">
                    <FaFileExcel className="file-icon" />
                  </span>
                  {intl.formatMessage(messages.ExportToExcel)}
                </Button>
              )}
            </div>
          </div>
        </div>

        {matrixType === 'capacity' ? (
          this.state.parametersForLastApiCall === null ? (
            <div>
              <LoadingAnimation />
            </div>
          ) : (
            <CapacityPlanningReportTable
              data={items}
              hideableColumns={hideableColumns || []}
              columns={columns}
              filters={filters}
              loading={this.state.capacityLoading}
              capacityColumnStatus={capacityColumnStatus}
            />
          )
        ) : (
          <ScrollableTable
            tableClassName="table-grid margin50B"
            loadItems={searchParams => this.loadItems(searchParams)}
            initialSearchParams={initialSearchParams}
            items={items}
            renderRow={item => this.renderRow(item)}
            noDataLabel={<FormattedMessage {...messages.NoEmployeesFound} />}
          >
            <ColumnHeader
              title={intl.formatMessage(messages.Employee)}
              sortKey={'employee'}
              style={{ width: '200px', tableLayout: 'fixed' }}
            />
            <ColumnHeader
              title={intl.formatMessage(messages.EmployeeRole)}
              sortKey={'employeeRole'}
              style={{ width: '200px', tableLayout: 'fixed' }}
            />

            {hideableColumns &&
              hideableColumns.map((col, index) => (
                <ColumnHeader
                  key={col.id}
                  title={col.label}
                  sortKey={col.id}
                  visible={columns.includes(index)}
                  style={{ width: '200px', tableLayout: 'fixed' }}
                />
              ))}

            <ColumnHeader title={intl.formatMessage(messages.Totals)} sortKey={'total'} />
          </ScrollableTable>
        )}
      </div>
    );
  }
}

AssignmentAnalysisMatrix.propTypes = {
  intl: intlShape.isRequired,
  matrixType: PropTypes.string,
};

AssignmentAnalysisMatrix.defaultProps = {
  matrixType: 'unknown',
};

export default injectStateService(injectIntl(AssignmentAnalysisMatrix));
